import * as React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { CheckoutOrderFragment, OrderFlight } from '@websky/graphql';
import { useTheme } from '../../theme';
import { useBookingPreview } from './BookingPreview.hook';
import OrderCities from '../../OrderCard/components/OrderPreview/OrderCities/OrderCities';
import Button from '../../BaseComponents/Button';
import Loader from '../../BaseComponents/Button/components/Loader/Loader';
import WarningModal from '../../WarningModal';
import { CdnIcon } from '@shared/ui';

export interface Props {
	className?: string;
	title?: string;
	order: CheckoutOrderFragment;
	onClearOrder?: () => void;
	onViewOrderClick?: () => void;
	isLoading?: boolean;
	redirectViaHistory: boolean;
	isOrderOwner?: boolean;
}

const BookingPreview: React.FC<Props> = props => {
	const { className, order, isLoading, title, isOrderOwner } = props;

	const { t } = useTranslation('BookingPreview');
	const { BookingPreview: css } = useTheme('BookingPreview');

	const model = useBookingPreview(props);

	if (isLoading) {
		return (
			<div className={cn(css.root, className)}>
				<div className={css.title}>{t('Searching flight')}</div>
				<div className={css.loader}>
					<Loader />
				</div>
			</div>
		);
	}

	return (
		<>
			<div className={cn(css.root, className)}>
				{props.onClearOrder && !model.isSaveLoading && (
					<div className={css.header}>
						<Button variant="text" className={css.clear} onClick={props.onClearOrder}>
							<CdnIcon icon={'Action/Close-Clear'} className={css.clear__icon} />
							{t('Reset search')}
						</Button>
					</div>
				)}

				<div className={css.title}>{title || t('Flight found')}</div>

				<div className={css.wrapper}>
					<div className={css.info_field}>
						<div>{order.locator}</div>
						<div className={css.info_field__travellers}>
							{model.travellersCount} {t('SearchForm:passenger', { count: model.travellersCount })}
						</div>
					</div>
					<div className={cn(css.info_field, css.info_field__cities)}>
						<OrderCities flight={order.flight as OrderFlight} bookingPreview={true} />
					</div>
					<div className={css.info_field}>{model.date}</div>
				</div>

				<div className={css.footer}>
					{/* Скрываем кнопку если пользователь залогинен и является владельцем найденого заказа  */}
					{!isOrderOwner && (
						<Button
							variant={'action'}
							className={cn(css.button)}
							isLoading={model.isLoading}
							onClick={model.onSaveAndView}
						>
							{t('View and add to cabinet')}
						</Button>
					)}

					{!model.isLoading && (
						<Button
							variant={'action'}
							className={cn(css.button, { [css.button_cancel]: !isOrderOwner })}
							onClick={model.onViewOrder}
						>
							{t('View booking')}
						</Button>
					)}
				</div>
			</div>

			<WarningModal
				title={t('Checkout:Oops...')}
				buttonText={t('Checkout:OK, thank you')}
				isOpen={!!model.error}
				content={t(model.error)}
				onClose={model.onClearError}
				onButtonClick={model.onClearError}
			/>
		</>
	);
};

export default BookingPreview;
